import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  CheckCircle2,
  Circle,
  Info,
  AlertTriangle,
  ExternalLink,
  Download,
  Rocket,
  Mail,
  CreditCard,
  Users,
  Instagram,
  Facebook,
  Linkedin,
  Twitter,
  Image as ImageIcon,
  CalendarDays,
  BarChart3,
  Shield,
  LifeBuoy,
} from "lucide-react";
import MarketingHeader from "@/components/layout/MarketingHeader";
import MarketingFooter from "@/components/layout/MarketingFooter";

/**
 * SetupGuide — the single document a new customer follows to go from
 * signup to publishing live posts across Instagram, Facebook, LinkedIn and X.
 *
 * This page is intentionally plain, scannable, and literal. It names the
 * screens, buttons and fields people will actually see on each provider so
 * they can follow along without guessing. If a provider changes a label the
 * copy here must be updated with it — treat this file as the source of truth.
 */

const SECTIONS = [
  { id: "overview", label: "Overview & what you need", icon: Rocket },
  { id: "signup", label: "1. Sign up & create workspace", icon: Users },
  { id: "verify", label: "2. Verify your email", icon: Mail },
  { id: "team", label: "3. Invite your team", icon: Users },
  { id: "billing", label: "4. Choose a plan (Razorpay)", icon: CreditCard },
  { id: "meta", label: "5. Connect Instagram + Facebook", icon: Instagram },
  { id: "linkedin", label: "6. Connect LinkedIn", icon: Linkedin },
  { id: "x", label: "7. Connect X (Twitter)", icon: Twitter },
  { id: "media", label: "8. Upload media", icon: ImageIcon },
  { id: "first-post", label: "9. Create your first post", icon: Rocket },
  { id: "approvals", label: "10. Approval workflow", icon: CheckCircle2 },
  { id: "calendar", label: "11. Schedule & calendar", icon: CalendarDays },
  { id: "analytics", label: "12. Analytics & insights", icon: BarChart3 },
  { id: "security", label: "13. Security & data", icon: Shield },
  { id: "faq", label: "14. Troubleshooting & FAQ", icon: LifeBuoy },
  { id: "support", label: "15. Getting help", icon: LifeBuoy },
];

export default function SetupGuide() {
  const [active, setActive] = useState("overview");

  useEffect(() => {
    document.title = "Setup Guide — SocialMind";
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) setActive(e.target.id);
        });
      },
      { rootMargin: "-30% 0px -60% 0px", threshold: 0 }
    );
    SECTIONS.forEach((s) => {
      const el = document.getElementById(s.id);
      if (el) obs.observe(el);
    });
    return () => obs.disconnect();
  }, []);

  return (
    <div className="bg-white" data-testid="setup-guide-page">
      <MarketingHeader />

      {/* Hero */}
      <section className="border-b border-[#E4E9EF] bg-[#F6F8FA]">
        <div className="mx-auto max-w-6xl px-6 py-14">
          <div className="inline-flex items-center gap-2 rounded-full bg-white px-3 py-1 text-xs font-semibold text-[#1F5FD8] shadow-sm">
            <Rocket className="h-3.5 w-3.5" />
            Customer onboarding
          </div>
          <h1 className="mt-4 font-display text-4xl font-bold tracking-tight text-[#0B2B33] sm:text-5xl">
            SocialMind setup guide
          </h1>
          <p className="mt-3 max-w-2xl text-lg text-[#43535E]">
            Everything you need to go from sign-up to publishing live posts across
            Instagram, Facebook, LinkedIn and X. Follow the steps in order — the
            whole thing takes about 30 minutes if your accounts are ready.
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            <Link to="/signup" className="hs-btn-primary text-sm" data-testid="setup-cta-signup">
              Start free trial
            </Link>
            <a
              href="/socialmind-setup-guide.pdf"
              download="SocialMind-Setup-Guide.pdf"
              data-testid="setup-download-pdf"
              className="inline-flex items-center gap-2 rounded-full border border-[#0B2B33] bg-white px-4 py-2 text-sm font-semibold text-[#0B2B33] shadow-sm transition-colors hover:bg-[#0B2B33] hover:text-white"
            >
              <Download className="h-4 w-4" />
              Download PDF
            </a>
            <a href="#overview" className="hs-btn-ghost text-sm">
              Read on this page
            </a>
          </div>
        </div>
      </section>

      <div className="mx-auto grid max-w-6xl grid-cols-1 gap-10 px-6 py-12 lg:grid-cols-[260px_1fr]">
        {/* Sticky TOC */}
        <aside className="hidden lg:block">
          <nav className="sticky top-24" aria-label="On this page">
            <div className="mb-3 text-xs font-semibold uppercase tracking-wider text-[#8A97A0]">
              On this page
            </div>
            <ul className="space-y-1">
              {SECTIONS.map((s) => (
                <li key={s.id}>
                  <a
                    href={`#${s.id}`}
                    data-testid={`toc-${s.id}`}
                    className={`block rounded-md px-3 py-1.5 text-sm transition-colors ${
                      active === s.id
                        ? "bg-[#EEF4FF] font-semibold text-[#1F5FD8]"
                        : "text-[#43535E] hover:bg-[#F6F8FA] hover:text-[#0B2B33]"
                    }`}
                  >
                    {s.label}
                  </a>
                </li>
              ))}
            </ul>
          </nav>
        </aside>

        {/* Content */}
        <main className="min-w-0 space-y-14">
          <Overview />
          <Signup />
          <VerifyEmail />
          <InviteTeam />
          <Billing />
          <ConnectMeta />
          <ConnectLinkedIn />
          <ConnectX />
          <UploadMedia />
          <FirstPost />
          <Approvals />
          <CalendarSection />
          <Analytics />
          <SecuritySection />
          <Faq />
          <Support />
        </main>
      </div>

      <MarketingFooter />
    </div>
  );
}

/* ---------------- Shared primitives ---------------- */

function Section({ id, title, kicker, icon: Icon, children }) {
  return (
    <section id={id} className="scroll-mt-28" data-testid={`section-${id}`}>
      <div className="flex items-center gap-3">
        {Icon ? (
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-[#EEF4FF] text-[#1F5FD8]">
            <Icon className="h-5 w-5" />
          </div>
        ) : null}
        <div>
          {kicker ? (
            <div className="text-xs font-semibold uppercase tracking-wider text-[#8A97A0]">
              {kicker}
            </div>
          ) : null}
          <h2 className="font-display text-2xl font-bold tracking-tight text-[#0B2B33] sm:text-3xl">
            {title}
          </h2>
        </div>
      </div>
      <div className="mt-5 space-y-4 text-[#43535E]">{children}</div>
    </section>
  );
}

function P({ children }) {
  return <p className="text-[#43535E] leading-relaxed">{children}</p>;
}

function Callout({ tone = "info", title, children }) {
  const toneMap = {
    info: {
      bg: "bg-[#EEF4FF]",
      border: "border-[#CFE0FF]",
      icon: <Info className="h-5 w-5 text-[#1F5FD8]" />,
    },
    warn: {
      bg: "bg-[#FFF4E2]",
      border: "border-[#F4D9A6]",
      icon: <AlertTriangle className="h-5 w-5 text-[#B26A00]" />,
    },
    success: {
      bg: "bg-[#E8F7EE]",
      border: "border-[#C3E7CF]",
      icon: <CheckCircle2 className="h-5 w-5 text-[#1F7A3D]" />,
    },
  };
  const t = toneMap[tone] || toneMap.info;
  return (
    <div className={`flex gap-3 rounded-xl border ${t.border} ${t.bg} p-4`}>
      <div className="mt-0.5 shrink-0">{t.icon}</div>
      <div className="text-sm text-[#0B2B33]">
        {title ? <div className="font-semibold">{title}</div> : null}
        <div className="mt-1 space-y-2 text-[#43535E]">{children}</div>
      </div>
    </div>
  );
}

function Steps({ items }) {
  return (
    <ol className="space-y-4">
      {items.map((it, idx) => (
        <li key={idx} className="flex gap-4">
          <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[#0B2B33] text-xs font-bold text-white">
            {idx + 1}
          </div>
          <div className="min-w-0 pt-0.5">
            {typeof it === "string" ? (
              <P>{it}</P>
            ) : (
              <>
                {it.title ? (
                  <div className="font-semibold text-[#0B2B33]">{it.title}</div>
                ) : null}
                {it.body ? <div className="mt-1 text-[#43535E]">{it.body}</div> : null}
              </>
            )}
          </div>
        </li>
      ))}
    </ol>
  );
}

function Checklist({ items }) {
  return (
    <ul className="space-y-2">
      {items.map((t, i) => (
        <li key={i} className="flex gap-2 text-sm text-[#0B2B33]">
          <Circle className="mt-0.5 h-4 w-4 shrink-0 text-[#8A97A0]" />
          <span>{t}</span>
        </li>
      ))}
    </ul>
  );
}

function FieldTable({ rows }) {
  return (
    <div className="overflow-hidden rounded-xl border border-[#E4E9EF]">
      <table className="min-w-full divide-y divide-[#E4E9EF] text-sm">
        <thead className="bg-[#F6F8FA]">
          <tr>
            <th className="px-4 py-2 text-left font-semibold text-[#0B2B33]">Field</th>
            <th className="px-4 py-2 text-left font-semibold text-[#0B2B33]">What to enter</th>
            <th className="px-4 py-2 text-left font-semibold text-[#0B2B33]">Required</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-[#E4E9EF] bg-white">
          {rows.map((r, i) => (
            <tr key={i}>
              <td className="whitespace-nowrap px-4 py-2 font-semibold text-[#0B2B33]">{r.field}</td>
              <td className="px-4 py-2 text-[#43535E]">{r.hint}</td>
              <td className="px-4 py-2 text-[#43535E]">{r.required ? "Yes" : "Optional"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function PermsTable({ rows }) {
  return (
    <div className="overflow-hidden rounded-xl border border-[#E4E9EF]">
      <table className="min-w-full divide-y divide-[#E4E9EF] text-sm">
        <thead className="bg-[#F6F8FA]">
          <tr>
            <th className="px-4 py-2 text-left font-semibold text-[#0B2B33]">Permission / scope</th>
            <th className="px-4 py-2 text-left font-semibold text-[#0B2B33]">Why we ask for it</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-[#E4E9EF] bg-white">
          {rows.map((r, i) => (
            <tr key={i}>
              <td className="whitespace-nowrap px-4 py-2 font-mono text-xs text-[#0B2B33]">{r.scope}</td>
              <td className="px-4 py-2 text-[#43535E]">{r.why}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ---------------- Content sections ---------------- */

function Overview() {
  return (
    <Section id="overview" title="Overview & what you need" kicker="Before you start" icon={Rocket}>
      <P>
        SocialMind is a workspace where your team plans, writes, approves, and publishes
        social posts across Instagram, Facebook, LinkedIn and X from one place. To use it
        end-to-end a customer needs three things ready: access to the social accounts they
        want to post to, a work email they control, and a payment method once the trial
        ends.
      </P>

      <div className="grid gap-4 sm:grid-cols-2">
        <div className="rounded-xl border border-[#E4E9EF] p-5">
          <div className="text-xs font-semibold uppercase tracking-wider text-[#8A97A0]">
            Accounts you must have
          </div>
          <ul className="mt-3 space-y-2 text-sm text-[#0B2B33]">
            <li>• A work email you can receive at (for verification)</li>
            <li>• A <b>Facebook Page</b> you administer (needed for Instagram too)</li>
            <li>• Your Instagram set to <b>Business or Creator</b> and linked to that Page</li>
            <li>• A <b>LinkedIn Company Page</b> where you are Super or Content admin</li>
            <li>• An <b>X (Twitter)</b> account with login access</li>
            <li>• A debit/credit card, UPI or netbanking for Razorpay</li>
          </ul>
        </div>
        <div className="rounded-xl border border-[#E4E9EF] p-5">
          <div className="text-xs font-semibold uppercase tracking-wider text-[#8A97A0]">
            Roles inside SocialMind
          </div>
          <ul className="mt-3 space-y-2 text-sm text-[#0B2B33]">
            <li><b>Admin</b> — billing, members, connected accounts, everything</li>
            <li><b>Editor</b> — drafts posts, uploads media, submits for approval</li>
            <li><b>Approver</b> — approves or rejects drafts, cannot publish directly</li>
            <li><b>Viewer</b> — read-only access to calendar and analytics</li>
          </ul>
        </div>
      </div>

      <Callout tone="info" title="Trial & billing">
        Every new workspace starts on a <b>14-day free trial</b> of the Pro plan — no card
        required to begin. You add payment when the trial ends or when you upgrade.
      </Callout>
    </Section>
  );
}

function Signup() {
  return (
    <Section id="signup" title="Sign up and create a workspace" kicker="Step 1" icon={Users}>
      <P>
        Go to <a href="/signup" className="font-semibold text-[#1F5FD8] hover:underline">/signup</a>{" "}
        and complete this form.
      </P>
      <FieldTable
        rows={[
          { field: "Full name", hint: "Your name as teammates should see it.", required: true },
          { field: "Work email", hint: "Use your company email. This becomes your login.", required: true },
          { field: "Password", hint: "At least 10 characters, mix of letters and digits.", required: true },
          { field: "Workspace name", hint: "Your company or brand (e.g. 'Northwind'). Shown in headers.", required: true },
          { field: "Industry", hint: "Picks sensible defaults for AI tone and hashtags.", required: false },
          { field: "Timezone", hint: "All scheduled times use this.", required: true },
          { field: "Default language", hint: "Primary language the AI writes in. Can be overridden per post.", required: true },
        ]}
      />
      <Callout tone="warn" title="One workspace per company">
        Every workspace is strictly isolated — users, posts, media and analytics cannot be
        seen across workspaces. If your agency manages several brands, create one
        workspace per brand or ask us about the Agency plan which lets you switch between
        them from one login.
      </Callout>
    </Section>
  );
}

function VerifyEmail() {
  return (
    <Section id="verify" title="Verify your email" kicker="Step 2" icon={Mail}>
      <Steps
        items={[
          "After signing up you'll receive an email from no-reply@socialmind.in with the subject 'Confirm your SocialMind account'.",
          "Open it and click 'Verify my email'. The link is valid for 24 hours.",
          "You'll land back on SocialMind, now signed in to your new workspace.",
        ]}
      />
      <Callout tone="info" title="Didn't get the email?">
        Check Spam/Promotions. If still missing, click <b>Resend verification</b> on the
        login screen. The sender is <code>no-reply@socialmind.in</code> — allow-list it in
        your mail provider to be safe.
      </Callout>
    </Section>
  );
}

function InviteTeam() {
  return (
    <Section id="team" title="Invite your team" kicker="Step 3" icon={Users}>
      <P>
        Open <b>Settings → Members</b> and click <b>Invite</b>. Each invite requires:
      </P>
      <FieldTable
        rows={[
          { field: "Email", hint: "The teammate's work email.", required: true },
          { field: "Role", hint: "Admin, Editor, Approver, or Viewer. See role matrix above.", required: true },
          { field: "Message (optional)", hint: "A note that goes inside the invite email.", required: false },
        ]}
      />
      <P>
        Each invited user receives an email with a one-click join link that expires in 7
        days. Pending invites are listed on the same page and can be revoked at any time.
      </P>
    </Section>
  );
}

function Billing() {
  return (
    <Section id="billing" title="Choose a plan and add payment" kicker="Step 4" icon={CreditCard}>
      <P>
        SocialMind charges via <b>Razorpay</b>. You'll be redirected to Razorpay's secure
        checkout — we never see or store your card details.
      </P>
      <Steps
        items={[
          { title: "Go to Settings → Billing", body: "Click 'Change plan' and pick Starter, Pro or Agency." },
          { title: "Fill billing details", body: "Business name, GSTIN (if applicable), billing address and country. These appear on your tax invoices." },
          { title: "Enter payment on Razorpay", body: "Card, UPI, netbanking or wallet. Razorpay auto-charges you on each renewal — you can cancel any time from Billing." },
          { title: "Receive invoice by email", body: "A GST-compliant invoice is emailed after every successful charge and is also available under Billing → Invoices." },
        ]}
      />
      <Callout tone="info" title="Switching plans">
        You can upgrade instantly — the difference is pro-rated on your next invoice.
        Downgrades take effect at the end of the current billing cycle so you don't lose
        features you've already paid for.
      </Callout>
    </Section>
  );
}

function ConnectMeta() {
  return (
    <Section
      id="meta"
      title="Connect Instagram & Facebook (via Meta)"
      kicker="Step 5"
      icon={Instagram}
    >
      <P>
        Instagram and Facebook are connected together through one authorization called{" "}
        <b>Facebook Login for Business</b>, because Instagram publishing requires a
        Facebook Page as its backbone. Read the prerequisites carefully — most "it
        didn't work" cases trace back here.
      </P>

      <div className="rounded-xl border border-[#E4E9EF] bg-[#F6F8FA] p-5">
        <div className="font-semibold text-[#0B2B33]">Prerequisites (do these inside Meta first)</div>
        <Checklist
          items={[
            "You have a Facebook account with your real name (personal profiles only, no fake accounts).",
            "You administer at least one Facebook Page. If not: on facebook.com → Menu → Pages → Create new Page.",
            "Your Instagram account is set to Business or Creator. Open Instagram app → Settings and privacy → Account type and tools → Switch to professional account.",
            "That Instagram account is linked to your Facebook Page. On Facebook: Page → Settings → Linked accounts → Instagram → Connect account.",
            "You are the Page admin (not just Editor). Check at Page → Settings → Page roles.",
          ]}
        />
      </div>

      <div>
        <div className="font-semibold text-[#0B2B33]">The connection flow</div>
        <Steps
          items={[
            { title: "In SocialMind, open Social Accounts", body: "Left sidebar → Social Accounts → tile 'Instagram + Facebook' → click Connect." },
            { title: "Log in to Facebook", body: "A Facebook popup opens. Log in with the account that administers the Page you want to post from. If you are already logged into Facebook in that browser you'll see your name and a Continue button instead." },
            { title: "Choose Business Portfolio (if prompted)", body: "If your Facebook account owns multiple businesses, Meta will ask which one SocialMind can access. Pick the one that owns the Page and Instagram you want to post from." },
            { title: "Pick the Pages", body: "Meta shows a list titled 'Which Pages do you want to use with SocialMind?'. Tick the Pages you want to publish to. You can select more than one." },
            { title: "Pick the Instagram accounts", body: "Below the Pages, Meta lists the Instagram accounts attached to those Pages. Tick the ones you want to publish to." },
            { title: "Review permissions", body: "Meta shows every permission SocialMind is requesting. Leave them all enabled — switching any off will cause publishing to fail. The full list is below." },
            { title: "Click Save", body: "You'll be redirected back to SocialMind with the Instagram and Facebook accounts now showing status 'Connected' and a green dot." },
          ]}
        />
      </div>

      <div>
        <div className="font-semibold text-[#0B2B33]">Permissions SocialMind requests from Meta</div>
        <PermsTable
          rows={[
            { scope: "pages_show_list", why: "Show you the Pages you administer so you can pick which to connect." },
            { scope: "pages_read_engagement", why: "Read likes/comments/reach counts to populate Analytics." },
            { scope: "pages_manage_posts", why: "Publish, edit and delete posts on the Pages you selected." },
            { scope: "pages_manage_metadata", why: "Register the webhook that notifies us when a scheduled post is actually delivered." },
            { scope: "instagram_basic", why: "Read your IG profile and media objects." },
            { scope: "instagram_content_publish", why: "Publish feed photos, carousels and reels on the IG accounts you selected." },
            { scope: "instagram_manage_insights", why: "Read Insights (reach, impressions, saves) to populate Analytics." },
            { scope: "business_management", why: "Confirm that your Facebook account administers the selected Business Portfolio." },
          ]}
        />
      </div>

      <Callout tone="warn" title="Common gotchas on Meta">
        <ul className="list-disc space-y-1 pl-5">
          <li><b>"No Instagram accounts found"</b> — IG is still Personal, not Business/Creator, or not linked to a Page.</li>
          <li><b>"You don't have permission"</b> — you're an Editor on the Page but not an Admin. Only Admins can authorize publishing apps.</li>
          <li><b>Cannot post Reels</b> — Reels require the video to be MP4, 9:16 aspect ratio, under 90 seconds and smaller than 100 MB.</li>
          <li><b>Instagram publishing cap</b> — Meta allows 25 published posts per IG account per 24 hours. SocialMind queues anything beyond that until the window reopens.</li>
        </ul>
      </Callout>

      <Callout tone="info" title="Changing the Pages or Instagram accounts later">
        Facebook → Settings → Business integrations → SocialMind → Edit. Add or remove
        Pages/IG accounts there and SocialMind will reflect the change on next sync.
      </Callout>
    </Section>
  );
}

function ConnectLinkedIn() {
  return (
    <Section id="linkedin" title="Connect LinkedIn" kicker="Step 6" icon={Linkedin}>
      <P>
        LinkedIn supports posting to <b>your personal profile</b> and to any{" "}
        <b>Company Page</b> you administer. SocialMind handles both; which one is used is
        decided per post in the composer.
      </P>

      <div className="rounded-xl border border-[#E4E9EF] bg-[#F6F8FA] p-5">
        <div className="font-semibold text-[#0B2B33]">Prerequisites</div>
        <Checklist
          items={[
            "A LinkedIn account you can sign into (same account used in a browser works best).",
            "For Company Page posting: you must be Super Admin or Content Admin on that page. Check at linkedin.com/company/your-company/admin/ → Admin tools → Manage admins.",
            "The Company Page must be published (not in draft).",
          ]}
        />
      </div>

      <div>
        <div className="font-semibold text-[#0B2B33]">The connection flow</div>
        <Steps
          items={[
            { title: "Open Social Accounts in SocialMind", body: "Click 'Connect LinkedIn' on the LinkedIn tile." },
            { title: "Sign into LinkedIn", body: "LinkedIn's OAuth screen opens. If prompted, complete 2FA." },
            { title: "Click 'Allow' on the authorization screen", body: "LinkedIn lists the exact scopes SocialMind is requesting. You must leave them all enabled." },
            { title: "Pick which Company Pages", body: "SocialMind reads the list of pages you administer and shows them in a dropdown. Tick each page you want to publish to." },
            { title: "Done", body: "You're back in SocialMind with LinkedIn now showing status 'Connected' and one entry per connected target (personal profile + each page)." },
          ]}
        />
      </div>

      <div>
        <div className="font-semibold text-[#0B2B33]">LinkedIn scopes requested</div>
        <PermsTable
          rows={[
            { scope: "openid, profile, email", why: "Read your name, headline and email so SocialMind knows which LinkedIn user has connected." },
            { scope: "w_member_social", why: "Publish posts to your personal profile." },
            { scope: "r_organization_social", why: "Read posts and engagement on the Company Pages you administer, to show them in Analytics." },
            { scope: "w_organization_social", why: "Publish posts to those Company Pages on your behalf." },
            { scope: "rw_organization_admin", why: "Fetch the list of Company Pages you administer so SocialMind can show them in the picker." },
          ]}
        />
      </div>

      <Callout tone="warn" title="LinkedIn limits">
        LinkedIn caps posting at <b>~150 API-published posts per user per day</b>, and
        rejects posts longer than <b>3,000 characters</b>, images over <b>5 MB</b>, or
        videos over <b>5 GB / 10 minutes</b>. SocialMind flags any draft that exceeds
        these before you submit for approval.
      </Callout>
    </Section>
  );
}

function ConnectX() {
  return (
    <Section id="x" title="Connect X (Twitter)" kicker="Step 7" icon={Twitter}>
      <P>
        X publishing uses OAuth 2.0 with a Confidential Client — SocialMind holds the
        developer app, so you do <b>not</b> need to apply to X's developer portal or pay
        for API access yourself. You only need to log in and authorize.
      </P>

      <div className="rounded-xl border border-[#E4E9EF] bg-[#F6F8FA] p-5">
        <div className="font-semibold text-[#0B2B33]">Prerequisites</div>
        <Checklist
          items={[
            "An X account you can sign into right now.",
            "Access to the account's email or phone for 2FA codes.",
            "If the X account was created in the last 24 hours, wait — X will reject OAuth from very new accounts.",
          ]}
        />
      </div>

      <div>
        <div className="font-semibold text-[#0B2B33]">The connection flow</div>
        <Steps
          items={[
            { title: "Open Social Accounts in SocialMind", body: "Click 'Connect X' on the X tile." },
            { title: "Sign into X", body: "X's authorization page opens. Enter username and password, complete 2FA if prompted." },
            { title: "Review and click 'Authorize app'", body: "X displays the scopes SocialMind is requesting. The username that is about to be connected is shown at the top — double-check it before clicking Authorize." },
            { title: "You're returned to SocialMind", body: "The X account is listed under Social Accounts with status 'Connected' and your @handle visible." },
          ]}
        />
      </div>

      <div>
        <div className="font-semibold text-[#0B2B33]">X scopes requested</div>
        <PermsTable
          rows={[
            { scope: "tweet.read", why: "Read your own posts for Analytics (impressions, likes, replies)." },
            { scope: "tweet.write", why: "Publish posts and threads on your behalf when a scheduled time arrives." },
            { scope: "users.read", why: "Read your handle and profile so SocialMind can show which account is connected." },
            { scope: "offline.access", why: "Receive a refresh token so SocialMind keeps publishing without asking you to log in every two hours." },
          ]}
        />
      </div>

      <Callout tone="warn" title="X limits">
        X restricts publishing to <b>500 posts per 24 hours</b> per connected account and{" "}
        <b>280 characters per post</b> (longer posts are rejected unless the account has X
        Premium, which allows up to 25,000 characters). Threads are supported — compose
        them in SocialMind as a sequence; each tweet in the thread counts separately
        against the daily limit.
      </Callout>

      <Callout tone="info" title="Re-authorizing">
        If a connected X account suddenly stops publishing it usually means the refresh
        token expired because you changed your X password or revoked the app at{" "}
        <code>x.com → Settings → Security → Apps and sessions</code>. Open Social Accounts
        in SocialMind and click <b>Reconnect</b> on the affected row.
      </Callout>
    </Section>
  );
}

function UploadMedia() {
  return (
    <Section id="media" title="Upload media" kicker="Step 8" icon={ImageIcon}>
      <P>
        The Media Library is where images and videos live before you attach them to a
        post. Everything you upload is private to your workspace and never served
        publicly except as part of a published social post.
      </P>
      <FieldTable
        rows={[
          { field: "Images", hint: "JPEG, PNG, WEBP. Max 25 MB each. Minimum 320×320 px for Instagram.", required: false },
          { field: "Videos", hint: "MP4 (H.264 + AAC). Max 100 MB each; max 90 seconds for Reels, 10 minutes for feed.", required: false },
          { field: "Alt text", hint: "Required by Instagram and strongly recommended for accessibility.", required: false },
          { field: "Tags", hint: "Freeform labels to help your team find assets later ('product-shot', 'q1-campaign').", required: false },
        ]}
      />
    </Section>
  );
}

function FirstPost() {
  return (
    <Section id="first-post" title="Create your first post" kicker="Step 9" icon={Rocket}>
      <Steps
        items={[
          { title: "Open the Composer", body: "Sidebar → Composer → New post." },
          { title: "Pick the networks", body: "Tick Instagram, Facebook, LinkedIn or X — any combination. The composer shows a live preview for each one as you type." },
          { title: "Write the caption once", body: "SocialMind splits a single caption into per-network variants automatically. You can override any variant inline." },
          { title: "Use AI Suggest", body: "Click 'Suggest' to generate caption + hashtags tailored to each network, informed by what has performed well in your workspace." },
          { title: "Attach media", body: "Pick from Media Library or drop in new files." },
          { title: "Schedule or submit", body: "If you have publishing rights, pick a time on the calendar and click Schedule. Otherwise click 'Submit for approval' — see next section." },
        ]}
      />
    </Section>
  );
}

function Approvals() {
  return (
    <Section id="approvals" title="Approval workflow" kicker="Step 10" icon={CheckCircle2}>
      <P>
        Posts move through states: <b>Draft → In review → Approved → Scheduled → Published</b>.
        Who can move a post between which states is set per workspace in{" "}
        <b>Settings → Approval policy</b>.
      </P>
      <Checklist
        items={[
          "Editors can create drafts and submit them for review.",
          "Approvers see pending posts under Approvals; they can approve, request changes with a comment, or reject.",
          "Only Admins (or whoever your policy names) can mark a post as 'Ready to publish'.",
          "Every state change is written to the audit log with the user, time, and comment.",
        ]}
      />
      <Callout tone="info" title="Review by someone outside your workspace">
        For client sign-off, use <b>Review Links</b>. Open a post → Share → Create review
        link. A public, read-only URL is minted; your client approves or comments without
        creating a SocialMind account. Links can be set to expire or revoked at any time.
      </Callout>
    </Section>
  );
}

function CalendarSection() {
  return (
    <Section id="calendar" title="Schedule & calendar" kicker="Step 11" icon={CalendarDays}>
      <P>
        The Calendar shows every scheduled, published, failed and draft-in-review post on
        a month/week/day grid. Drag a post to reschedule. Click a slot to compose directly
        into that time.
      </P>
      <Checklist
        items={[
          "Scheduling uses your workspace timezone (set during signup).",
          "Each scheduled post retries up to 3 times on provider errors before it is marked Failed and notified to Admins.",
          "The 'Best time to post' badge on each day uses your own analytics; it needs ~2 weeks of history to appear.",
          "You can set a 'quiet hours' window in Settings so nothing publishes overnight.",
        ]}
      />
    </Section>
  );
}

function Analytics() {
  return (
    <Section id="analytics" title="Analytics & insights" kicker="Step 12" icon={BarChart3}>
      <P>
        Analytics pulls reach, impressions, engagement and follower delta from each
        connected provider. The <b>Learning Loop</b> turns that history into concrete
        recommendations — which day/time performs best per network, which hashtags lift
        reach, and which post lengths engage most.
      </P>
      <Callout tone="info" title="Data freshness">
        Instagram and Facebook data refreshes every 15 minutes. LinkedIn refreshes every
        hour (API limit). X refreshes every 5 minutes. All numbers shown in Analytics
        carry a 'last synced' timestamp.
      </Callout>
    </Section>
  );
}

function SecuritySection() {
  return (
    <Section id="security" title="Security & your data" kicker="Step 13" icon={Shield}>
      <Checklist
        items={[
          "All provider tokens (Meta, LinkedIn, X) are AES-256 encrypted at rest and never shown in the UI or exports.",
          "Every workspace is strictly isolated — one tenant cannot query another's data at the application or database layer.",
          "Admins can turn on required 2FA under Settings → Security. SSO (Google / Microsoft / Okta) is available on the Agency plan.",
          "You can export every record held for your workspace as JSON at any time, and deleting the workspace permanently removes it within 7 days.",
          "SocialMind support can only read your workspace with a written reason, read-only, for 30 minutes — and every grant is written to your own audit log.",
        ]}
      />
    </Section>
  );
}

function Faq() {
  const items = [
    {
      q: "A connected account went red — what happened?",
      a: "The provider revoked SocialMind's token. Usually caused by: password change, 2FA reset, or the user removing SocialMind from their account's connected apps list. Open Social Accounts → click Reconnect on the affected row and re-authorize.",
    },
    {
      q: "A scheduled post is in 'Failed' state",
      a: "Open the post → Activity tab. The exact provider error is logged there (e.g. 'Instagram: video aspect ratio must be 9:16'). Fix the issue and click Retry.",
    },
    {
      q: "AI suggestions feel generic",
      a: "Give the Learning Loop two weeks of data. Then under the Composer click AI Suggest → 'Use my brand voice' which pulls style from your three top-performing posts.",
    },
    {
      q: "Can we post to multiple Instagram accounts from one workspace?",
      a: "Yes. Connect as many as you administer; each shows up as its own target in the composer. The Pro plan includes 10 connected accounts, Agency is unlimited.",
    },
    {
      q: "Does SocialMind post Stories or DMs?",
      a: "Stories are supported on Instagram and Facebook. DMs are not — Meta does not permit third-party apps to send DMs on your behalf except through Messenger Business, which is a separate integration not included in SocialMind today.",
    },
    {
      q: "What happens if we exceed our plan limits?",
      a: "You keep publishing — SocialMind will not block posts. Overage is shown in Billing and charged on the next invoice at the plan's overage rate. You can set a hard cap in Settings → Billing to be refused instead of charged.",
    },
    {
      q: "Can we cancel any time?",
      a: "Yes. Settings → Billing → Cancel plan. You keep access until the end of the current billing cycle; after that the workspace moves to read-only. Data is retained for 90 days so you can re-subscribe and pick up where you left off.",
    },
  ];
  return (
    <Section id="faq" title="Troubleshooting & FAQ" kicker="Step 14" icon={LifeBuoy}>
      <div className="divide-y divide-[#E4E9EF] overflow-hidden rounded-xl border border-[#E4E9EF] bg-white">
        {items.map((it, i) => (
          <details key={i} className="group px-5 py-4">
            <summary className="flex cursor-pointer list-none items-center justify-between font-semibold text-[#0B2B33]">
              <span>{it.q}</span>
              <span className="ml-4 text-[#8A97A0] transition-transform group-open:rotate-45">+</span>
            </summary>
            <p className="mt-3 text-sm text-[#43535E]">{it.a}</p>
          </details>
        ))}
      </div>
    </Section>
  );
}

function Support() {
  const contact = process.env.REACT_APP_CONTACT_EMAIL || "hello@socialmind.in";
  return (
    <Section id="support" title="Getting help" kicker="Step 15" icon={LifeBuoy}>
      <div className="grid gap-4 sm:grid-cols-3">
        <div className="rounded-xl border border-[#E4E9EF] p-5">
          <div className="text-xs font-semibold uppercase tracking-wider text-[#8A97A0]">Email</div>
          <a href={`mailto:${contact}`} className="mt-2 block font-semibold text-[#1F5FD8] hover:underline">
            {contact}
          </a>
          <p className="mt-2 text-sm text-[#43535E]">Replies within 1 business day on Starter, 4 hours on Pro, 1 hour on Agency.</p>
        </div>
        <div className="rounded-xl border border-[#E4E9EF] p-5">
          <div className="text-xs font-semibold uppercase tracking-wider text-[#8A97A0]">Status</div>
          <a href="/#" className="mt-2 block font-semibold text-[#1F5FD8] hover:underline">
            status.socialmind.in <ExternalLink className="inline h-3.5 w-3.5" />
          </a>
          <p className="mt-2 text-sm text-[#43535E]">Live uptime per subsystem and incident history.</p>
        </div>
        <div className="rounded-xl border border-[#E4E9EF] p-5">
          <div className="text-xs font-semibold uppercase tracking-wider text-[#8A97A0]">In-app</div>
          <p className="mt-2 font-semibold text-[#0B2B33]">Help menu → Ask support</p>
          <p className="mt-2 text-sm text-[#43535E]">Attaches your workspace ID and recent actions automatically so we can look up context faster.</p>
        </div>
      </div>
    </Section>
  );
}
